import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Linkedin, Send, CheckCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      inquiryType: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formspree.io/f/mqakppov', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          inquiryType: formData.inquiryType,
          message: formData.message
        })
      });

      if (response.ok) {
        toast({
          title: 'Message Sent!',
          description: 'Thank you for reaching out! I\'ll get back to you soon.',
          duration: 5000
        });
        setFormData({ name: '', email: '', inquiryType: '', message: '' });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: 'Submission Failed',
        description: 'Please try again later or email directly at wp2020@gmail.com',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-black to-[#0B1D2A] relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Let's Create Something <span className="text-[#D4AF37]">That Matters</span>
          </h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto rounded-full mb-6" />
          <p
            className="text-lg text-white/70 max-w-2xl mx-auto"
            style={{ fontFamily: 'Lato, sans-serif' }}
          >
            If you're a creator, entrepreneur, educator, or visionary who believes in a better future for humanity — let's connect.
          </p>
        </div>

        {/* Connect with Charles - B&W Portrait Section */}
        <div className="mb-16">
          <Card className="bg-[#0B1D2A]/60 border-[#D4AF37]/30 p-8 backdrop-blur-sm overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* B&W Portrait */}
              <div className="relative">
                <div className="absolute inset-0 bg-[#D4AF37]/10 rounded-2xl blur-2xl" />
                <div className="relative aspect-square rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30 shadow-2xl">
                  <img
                    src="/bw-portrait.png"
                    alt="Charles Crawshaw - Professional Portrait"
                    className="w-full h-full object-cover grayscale"
                    onError={(e) => {
                      // Fallback to contemplative photo if B&W not available
                      e.target.src = '/contemplative-charles.png';
                      e.target.classList.add('grayscale');
                    }}
                  />
                </div>
              </div>

              {/* Connect Message */}
              <div className="space-y-6">
                <h3
                  className="text-3xl font-bold text-white"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Connect with <span className="text-[#D4AF37]">Charles</span>
                </h3>
                <p
                  className="text-lg text-white/80 leading-relaxed"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Whether it's AI, education, media, or global impact initiatives, I'm always open to collaborations that make a difference.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-sm text-white/60 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                        Email
                      </p>
                      <a
                        href="mailto:wp2020@gmail.com"
                        className="text-white hover:text-[#D4AF37] transition-colors font-medium text-lg"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      >
                        wp2020@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <Linkedin className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-sm text-white/60 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                        LinkedIn
                      </p>
                      <a
                        href="https://linkedin.com/in/charlescrawshaw"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white hover:text-[#D4AF37] transition-colors font-medium text-lg"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      >
                        linkedin.com/in/charlescrawshaw
                      </a>
                    </div>
                  </div>
                </div>
                <div className="pt-4">
                  <p
                    className="text-[#D4AF37] font-semibold text-xl italic"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    "Together, we can build a world that works for everyone."
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3
                className="text-2xl font-bold text-white mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Get In Touch
              </h3>
              <p
                className="text-white/70 leading-relaxed mb-6"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                Whether it's AI, education, media, or global impact initiatives, I'm always open to collaborations that make a difference.
              </p>
            </div>

            {/* Contact Cards */}
            <Card className="bg-[#0B1D2A]/60 border-[#D4AF37]/20 p-6 hover:border-[#D4AF37] transition-all duration-300 backdrop-blur-sm">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p
                    className="text-sm text-white/60 mb-1"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  >
                    Email
                  </p>
                  <a
                    href="mailto:wp2020@gmail.com"
                    className="text-white hover:text-[#D4AF37] transition-colors font-medium"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  >
                    wp2020@gmail.com
                  </a>
                </div>
              </div>
            </Card>

            <Card className="bg-[#0B1D2A]/60 border-[#D4AF37]/20 p-6 hover:border-[#D4AF37] transition-all duration-300 backdrop-blur-sm">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Linkedin className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p
                    className="text-sm text-white/60 mb-1"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  >
                    LinkedIn
                  </p>
                  <a
                    href="https://linkedin.com/in/charlescrawshaw"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-[#D4AF37] transition-colors font-medium"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  >
                    linkedin.com/in/charlescrawshaw
                  </a>
                </div>
              </div>
            </Card>

            <div className="p-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
              <p
                className="text-[#D4AF37] font-semibold text-lg"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                "Together, we can build a world that works for everyone."
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="bg-[#0B1D2A]/60 border-[#D4AF37]/20 p-8 backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-white mb-2 font-medium"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Your Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="bg-black/40 border-[#D4AF37]/30 text-white placeholder:text-white/40 focus:border-[#D4AF37]"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-white mb-2 font-medium"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="bg-black/40 border-[#D4AF37]/30 text-white placeholder:text-white/40 focus:border-[#D4AF37]"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <label
                  htmlFor="inquiryType"
                  className="block text-white mb-2 font-medium"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  I'm Interested In
                </label>
                <Select onValueChange={handleSelectChange} value={formData.inquiryType} required>
                  <SelectTrigger className="bg-black/40 border-[#D4AF37]/30 text-white focus:border-[#D4AF37]">
                    <SelectValue placeholder="Select inquiry type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1D2A] border-[#D4AF37]/30">
                    <SelectItem value="collaboration" className="text-white hover:bg-[#D4AF37]/20">
                      Collaboration
                    </SelectItem>
                    <SelectItem value="speaking" className="text-white hover:bg-[#D4AF37]/20">
                      Speaking
                    </SelectItem>
                    <SelectItem value="ai-projects" className="text-white hover:bg-[#D4AF37]/20">
                      AI Projects
                    </SelectItem>
                    <SelectItem value="media-press" className="text-white hover:bg-[#D4AF37]/20">
                      Media / Press
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="block text-white mb-2 font-medium"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Your Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me about your vision..."
                  rows={5}
                  className="bg-black/40 border-[#D4AF37]/30 text-white placeholder:text-white/40 focus:border-[#D4AF37] resize-none"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-semibold py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#D4AF37]/50"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                {isSubmitting ? (
                  <>Sending...</>
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
